import { useEffect, useLayoutEffect, useRef } from 'react';

export interface HotkeyOptions {
  /**
   * If false, ignores keystrokes when focus is in an editable element
   * (input, textarea, select, contenteditable). Defaults to true so
   * accidental shortcut firing while the user is typing is suppressed.
   */
  ignoreInEditable?: boolean;
  /** Disable the binding without unmounting consumers. */
  enabled?: boolean;
  /**
   * Where to attach the listener. Defaults to the window so shortcuts
   * keep working regardless of where focus currently is.
   */
  target?: Window | Document | HTMLElement | null;
}

interface Combo {
  key: string;
  ctrl?: boolean;
  meta?: boolean;
  alt?: boolean;
  shift?: boolean;
  /** True when the combo was specified as `mod+x` (Cmd on mac / Ctrl elsewhere). */
  mod?: boolean;
}

// `navigator.platform` is officially deprecated but still the most reliable
// signal for "this is a Mac" since `userAgentData` isn't shipped in Safari
// or Firefox. We read it once, behind a feature check, with a non-Mac
// fallback so the worst case is consistent cross-platform behaviour.
const isMac =
  typeof navigator !== 'undefined' &&
  /Mac|iPod|iPhone|iPad/.test(
    (navigator as Navigator & { userAgentData?: { platform?: string } }).userAgentData?.platform ??
       
      navigator.platform,
  );

function parse(combo: string): Combo {
  const parts = combo
    .toLowerCase()
    .split('+')
    .map((p) => p.trim())
    .filter(Boolean);
  const result: Combo = { key: '' };
  for (const part of parts) {
    if (part === 'ctrl') result.ctrl = true;
    else if (part === 'meta' || part === 'cmd') result.meta = true;
    else if (part === 'alt' || part === 'option') result.alt = true;
    else if (part === 'shift') result.shift = true;
    else if (part === 'mod') result.mod = true;
    else result.key = part;
  }
  return result;
}

function matches(event: KeyboardEvent, combo: Combo): boolean {
  if (event.key.toLowerCase() !== combo.key) return false;
  if (combo.mod) {
    if (!(isMac ? event.metaKey : event.ctrlKey)) return false;
  } else {
    if (Boolean(combo.ctrl) !== event.ctrlKey) return false;
    if (Boolean(combo.meta) !== event.metaKey) return false;
  }
  if (Boolean(combo.alt) !== event.altKey) return false;
  if (Boolean(combo.shift) !== event.shiftKey) return false;
  return true;
}

function isEditable(target: EventTarget | null): boolean {
  if (!(target instanceof HTMLElement)) return false;
  const tag = target.tagName;
  if (tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT') return true;
  return target.isContentEditable;
}

/**
 * Bind a keyboard shortcut. Combos are written as a `+`-joined string
 * (`'mod+k'`, `'shift+?'`, `'esc'`). `mod` resolves to ⌘ on macOS and
 * Ctrl elsewhere — preferred over hardcoding so apps stay platform-correct.
 */
export function useHotkey(
  combo: string,
  handler: (event: KeyboardEvent) => void,
  options: HotkeyOptions = {},
): void {
  const { ignoreInEditable = true, enabled = true, target } = options;
  const handlerRef = useRef(handler);
  // Keep the ref pointing at the latest handler without writing during render —
  // useLayoutEffect runs synchronously after commit and before the next paint,
  // matching the timing of the previous (illegal) inline assignment.
  useLayoutEffect(() => {
    handlerRef.current = handler;
  }, [handler]);

  useEffect(() => {
    if (!enabled) return;
    const node: EventTarget | null = target ?? (typeof window !== 'undefined' ? window : null);
    if (!node) return;

    const parsed = parse(combo);
    const onKey = (e: Event) => {
      const event = e as KeyboardEvent;
      if (!matches(event, parsed)) return;
      if (ignoreInEditable && isEditable(event.target)) return;
      handlerRef.current(event);
    };

    node.addEventListener('keydown', onKey);
    return () => {
      node.removeEventListener('keydown', onKey);
    };
  }, [combo, enabled, ignoreInEditable, target]);
}
